// Read-only journal-review correction preview: per transaction status, source, existing journal and proposed effect.
import { useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { exportToCsv } from '@/lib/exportCsv';
import { Button } from '@/components/ui/button';

const STATUSES = ['Posting Failed — Retry', 'Journal Missing', 'Pending Approval / Possible Duplicate', 'Needs Manual Review', 'Journal Exists — Link Missing'];
const retry = async (id, reload) => {
  try {
    const { data } = await api.post(`/salary-payments/${id}/retry-journal`);
    (['posted', 'linked', 'already_posted'].includes(data.status) ? toast.success : toast.error)(`Retry: ${data.status}`);
    reload();
  } catch (e) { toast.error(e?.response?.data?.detail || 'Retry failed'); }
};
const je = (r) => (r.existing_journals || []).map((j) => `${j.entry_number || j.id} ${j.reference} ${j.date} ${j.amount}`).join(' | ');
const COLS = [
  { key: 'status', label: 'Status' }, { key: 'source', label: 'Source' }, { key: 'reference', label: 'Reference' }, { key: 'id', label: 'Record id' },
  { key: 'date', label: 'Date' }, { key: 'amount', label: 'Amount' }, { key: 'branch', label: 'Branch' }, { key: 'payment_mode', label: 'Payment mode' },
  { key: 'party', label: 'Party' }, { key: 'bank_account', label: 'Statement bank account' }, { key: 'existing', label: 'Existing journal', format: (_, r) => je(r) },
  { key: 'verdict', label: 'Duplicate check' }, { key: 'dups', label: 'Possible duplicates', format: (_, r) => (r.possible_duplicates || []).map((d) => `${d.id.slice(0, 8)} ${d.date} ${d.amount} ${d.payment_mode || ''}`).join(' | ') },
  { key: 'proposed', label: 'Proposed correction' }, { key: 'effect', label: 'Accounting effect' },
];

export function JournalCorrectionPreview({ branchName }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('Journal Missing');
  const load = async () => {
    setBusy(true);
    try { setD((await api.get('/accounting/journal-review/preview', { timeout: 120000 })).data); } catch (e) { toast.error(e?.response?.data?.detail || 'Preview failed'); } finally { setBusy(false); }
  };
  if (!d) {
    return <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={load} disabled={busy} data-testid="journal-correction-preview-btn">{busy ? 'Building preview…' : 'Correction preview (read-only)'}</Button>;
  }
  const rows = d.items.filter((i) => i.status === filter).map((i) => ({ ...i, branch: branchName(i.branch_id || i.proposed_branch_id) }));
  return (
    <div className="w-full space-y-2" data-testid="journal-correction-preview">
      <p className="text-[11px] text-muted-foreground">{d.note}</p>
      <div className="flex flex-wrap gap-1.5">
        {STATUSES.map((s) => (
          <Button key={s} size="sm" variant={filter === s ? 'default' : 'outline'} className="rounded-full text-[11px] h-7" onClick={() => setFilter(s)} data-testid={`jcp-filter-${s.split(' ')[0].toLowerCase()}-${s.length}`}>
            {s} · {d.counts[s] || 0}{d.amounts[s] ? ` · SAR ${d.amounts[s].toFixed(2)}` : ''}
          </Button>
        ))}
        <span className="text-[11px] text-muted-foreground self-center" data-testid="jcp-no-action">Already Posted — No Action · {d.counts['Already Posted — No Action'] || 0} · on posting hold {d.counts.held || 0}</span>
        <Button size="sm" variant="ghost" className="text-[11px] h-7" onClick={() => exportToCsv(`journal-correction-preview-${new Date().toISOString().slice(0, 10)}`, d.items.map((i) => ({ ...i, branch: branchName(i.branch_id || i.proposed_branch_id) })), COLS)} data-testid="jcp-download-csv">Download CSV (all)</Button>
      </div>
      <div className="max-h-96 overflow-auto rounded border bg-white dark:bg-slate-900">
        <table className="w-full text-[11px]" data-testid="jcp-table">
          <thead className="bg-muted sticky top-0"><tr>{['Source', 'Reference', 'Date', 'Branch', 'Mode', 'Party', 'Amount', 'Existing journal / check', 'Proposed correction & effect'].map((h) => <th key={h} className="text-left px-2 py-1">{h}</th>)}</tr></thead>
          <tbody>
            {rows.slice(0, 300).map((r) => (
              <tr key={`${r.source}-${r.id}`} className="border-t align-top" data-testid={`jcp-row-${r.id}`}>
                <td className="px-2 py-1">{r.source}</td><td className="px-2 py-1 font-mono">{r.reference}</td><td className="px-2 py-1">{r.date}</td>
                <td className="px-2 py-1">{r.branch}</td><td className="px-2 py-1">{r.payment_mode || '—'}{r.bank_account ? ` · ${r.bank_account}` : ''}</td>
                <td className="px-2 py-1">{r.party}</td><td className="px-2 py-1 text-right font-mono">{Number(r.amount).toFixed(2)}</td>
                <td className="px-2 py-1">{je(r) || r.verdict || '—'}</td>
                <td className="px-2 py-1"><div>{r.proposed}</div><div className="text-muted-foreground">{r.effect}</div>
                  {r.status === 'Posting Failed — Retry' && <Button size="sm" variant="outline" className="h-6 mt-1 text-[11px]" onClick={() => retry(r.id, load)} data-testid={`jcp-retry-${r.id}`}>Retry posting</Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > 300 && <div className="p-2 text-[11px] text-muted-foreground">Showing 300 of {rows.length} — download the CSV for all rows.</div>}
      </div>
    </div>
  );
}
